/**
 * HS-samples-to-MediaStream bridge.
 *
 * The HD Steth streams 6-bit heart-sound samples (0..63) over BLE, batched
 * onto socket.io. The FE Stethoscope component (components/group/Stethoscope.tsx)
 * and the existing useAudioRecorder both expect a real MediaStream from a
 * MediaDeviceInfo-shaped device. This bridge constructs one synthetically
 * so both keep working without a code change.
 *
 * Pipeline:
 *   socket batch -> ring buffer -> AudioWorklet -> HPF 20 Hz -> LPF 300 Hz
 *     -> headroom gain -> one MediaStreamAudioDestinationNode per consumer
 *
 * The device's HS sample rate was measured at 640 Hz on real hardware
 * (2026-09-03); the caller pins it via setSourceRate() and we linearly
 * upsample into the AudioContext's native rate. The low-pass removes the
 * interpolation images above the device's 320 Hz Nyquist, which otherwise
 * sound harsh; heart sounds live well below that.
 *
 * Consumers get their own destination node via createOutputStream(): Chrome
 * does not carry audio into MediaStream.clone() of a destination-node track
 * (it reads as silence), and the RTC services stop the tracks they are
 * handed, so sharing one track is not an option either.
 */

import { HDStethBatch } from './types'

const WORKLET_NAME = 'hdsteth-hs-processor'
/** Below the lowest heart-sound content; also removes the 6-bit DC offset. */
const HIGHPASS_HZ = 20
/** Just under the device's Nyquist (640 Hz / 2); kills interpolation images. */
const LOWPASS_HZ = 300
/** Keep the 6-bit full-scale mapping away from clipping downstream. */
const HEADROOM_GAIN = 0.8

const WORKLET_CODE = `
class HDStethHsProcessor extends AudioWorkletProcessor {
  constructor() {
    super()
    this.buffer = new Float32Array(48000) // ~1s headroom at 48k
    this.read = 0
    this.write = 0
    this.size = 0
    this.port.onmessage = (e) => {
      const samples = e.data
      for (let i = 0; i < samples.length; i++) {
        this.buffer[this.write] = samples[i]
        this.write = (this.write + 1) % this.buffer.length
        if (this.size < this.buffer.length) this.size++
        else this.read = (this.read + 1) % this.buffer.length
      }
    }
  }
  process(_inputs, outputs) {
    const out = outputs[0][0]
    for (let i = 0; i < out.length; i++) {
      if (this.size > 0) {
        out[i] = this.buffer[this.read]
        this.read = (this.read + 1) % this.buffer.length
        this.size--
      } else {
        out[i] = 0
      }
    }
    return true
  }
}
registerProcessor('${WORKLET_NAME}', HDStethHsProcessor)
`

export class HDStethAudioBridge {
  private ctx: AudioContext | null = null
  private workletNode: AudioWorkletNode | null = null
  /** Last node of the shared conditioning chain; consumers' destinations hang off it. */
  private output: AudioNode | null = null
  private destination: MediaStreamAudioDestinationNode | null = null
  private consumerDestinations = new Set<MediaStreamAudioDestinationNode>()
  private ready: Promise<void> | null = null
  private hsRateHz = 1000
  private resampleAcc = 0

  async start(): Promise<MediaStream> {
    if (this.destination && this.ready) {
      await this.ready
      return this.destination.stream
    }
    const ctx = new AudioContext()
    this.ctx = ctx
    const blob = new Blob([WORKLET_CODE], { type: 'application/javascript' })
    const url = URL.createObjectURL(blob)
    this.ready = ctx.audioWorklet.addModule(url).then(() => {
      URL.revokeObjectURL(url)
      this.workletNode = new AudioWorkletNode(ctx, WORKLET_NAME)

      const highpass = ctx.createBiquadFilter()
      highpass.type = 'highpass'
      highpass.frequency.value = HIGHPASS_HZ
      highpass.Q.value = 0.7

      const lowpass = ctx.createBiquadFilter()
      lowpass.type = 'lowpass'
      lowpass.frequency.value = LOWPASS_HZ
      lowpass.Q.value = 0.7

      const gain = ctx.createGain()
      gain.gain.value = HEADROOM_GAIN

      this.workletNode.connect(highpass).connect(lowpass).connect(gain)
      this.output = gain

      this.destination = ctx.createMediaStreamDestination()
      gain.connect(this.destination)
    })
    await this.ready
    return this.destination!.stream
  }

  /**
   * A dedicated output stream for one consumer, backed by its own
   * destination node. Stopping its track detaches it without affecting
   * other consumers. Null until start() has completed.
   */
  createOutputStream(): MediaStream | null {
    if (!this.ctx || !this.output) return null
    const destination = this.ctx.createMediaStreamDestination()
    this.output.connect(destination)
    this.consumerDestinations.add(destination)
    for (const track of destination.stream.getAudioTracks()) {
      track.addEventListener('ended', () => this.releaseDestination(destination), { once: true })
    }
    return destination.stream
  }

  private releaseDestination(destination: MediaStreamAudioDestinationNode): void {
    if (!this.consumerDestinations.delete(destination)) return
    try { this.output?.disconnect(destination) } catch (e) { /* swallow */ }
  }

  /**
   * Resume the underlying AudioContext. Browsers start it suspended until a
   * user gesture, and a suspended context's worklet produces no audio — so the
   * synthetic MediaStream would be silent. Call this from a click handler.
   */
  async resume(): Promise<void> {
    if (this.ctx && this.ctx.state === 'suspended') {
      try { await this.ctx.resume() } catch (e) { /* swallow */ }
    }
  }

  stop(): void {
    try { this.workletNode?.disconnect() } catch (e) { /* swallow */ }
    try { this.output?.disconnect() } catch (e) { /* swallow */ }
    try { this.destination?.disconnect() } catch (e) { /* swallow */ }
    this.consumerDestinations.clear()
    this.workletNode = null
    this.output = null
    this.destination = null
    if (this.ctx && this.ctx.state !== 'closed') this.ctx.close().catch(() => { /* swallow */ })
    this.ctx = null
    this.ready = null
  }

  /** Set the assumed device HS sample rate in Hz once measured. */
  setSourceRate(hz: number): void {
    if (hz > 0) this.hsRateHz = hz
  }

  pushBatch(batch: HDStethBatch): void {
    if (!this.workletNode || !this.ctx) return
    if (batch.hs.length === 0) return
    const ratio = this.ctx.sampleRate / this.hsRateHz
    // Linear interpolation upsample from device rate to AudioContext rate.
    const inLen = batch.hs.length
    const outLen = Math.max(1, Math.floor(inLen * ratio + this.resampleAcc))
    const out = new Float32Array(outLen)
    let outIdx = 0
    let pos = -this.resampleAcc
    while (outIdx < outLen) {
      const i0 = Math.floor(pos)
      const frac = pos - i0
      const a = i0 >= 0 && i0 < inLen ? batch.hs[i0] : (i0 < 0 ? batch.hs[0] : batch.hs[inLen - 1])
      const b = i0 + 1 < inLen ? batch.hs[i0 + 1] : a
      const sample6 = a + (b - a) * frac
      // Map 0..63 (DC ~31) to roughly -1..+1.
      out[outIdx++] = (sample6 - 31.5) / 31.5
      pos += 1 / ratio
    }
    this.resampleAcc = (inLen * ratio + this.resampleAcc) - outLen
    this.workletNode.port.postMessage(out)
  }
}
